import { Box, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FormCheckbox from "../../forms/form-checkbox/FormCheckbox";
import FormPropertySection from '../../forms/form-property-section/FormPropertySection';
import FormProperty from '../../forms/form-property/FormProperty';
import FormRadioGroup, { FormRadioGroupRadioButton } from '../../forms/form-radio-group/FormRadioGroup';
import FormSelect from "../../forms/form-select/FormSelect";
import { isMobile, isTablet } from 'react-device-detect';
import { AccountSettingsFormType, deviceViews, groupViews } from '../AccountSettingUtils';
import { useFormContext } from 'react-hook-form';
import { SampleGrid } from './SampleGrid';
import { GaugeSheetViewMode } from '../../../views/gauge-sheet-view/GaugeSheetState';

export type AccountPreferencesProps = {
    visible: boolean;
};

const radioButtons: FormRadioGroupRadioButton[] = [{
    label: 'Light',
    ariaLabel: 'light theme',
    value: 'light'
}, {
    label: 'Dark',
    ariaLabel: 'dark theme',
    value: 'dark'
}];



export default function AccountPreferences({ visible }: AccountPreferencesProps) {

    const { t } = useTranslation();
    const isPhone = isMobile && !isTablet;
    const {
        watch,
        getValues,
        setValue
    } = useFormContext<AccountSettingsFormType>();

    const [gridCellPadding, gridFontSize] = watch([
        "gridCellPadding",
        "gridFontSize"
    ]);


    function onShowDeviceChange(checked: boolean) {

        const tankChecked = getValues('gaugeSheetShowTanks');

        if (!checked && !tankChecked) {
            setValue('gaugeSheetShowTanks', true, {shouldDirty: true});
        }

    }

    function onShowTanksChange(checked: boolean) {
        const deviceChecked = getValues('gaugeSheetShowDevice');

        if (!checked && !deviceChecked) {
            setValue('gaugeSheetShowDevice', true, {shouldDirty: true});
        }
        
    }

    return visible ?
        <>

            <FormPropertySection label={t('profile.chooseTheme')}>

                <FormProperty>

                    <FormRadioGroup label=''
                        name='theme'
                        radioButtons={radioButtons} />

                </FormProperty>

            </FormPropertySection>


            <FormPropertySection label={t('profile.generalPreferences')} >


                <FormProperty>
                    {/* https://uxplanet.org/checkbox-vs-toggle-switch-7fc6e83f10b8 */}
                    <FormCheckbox
                        name="offlineMode"
                        ariaLabel='offline mode enable'
                        label={t('profile.offlineMode')}
                        sx={{ width: '90%' }}
                    />

                </FormProperty>

            </FormPropertySection>

            <FormPropertySection label="">

                <FormProperty>
                    <FormSelect
                        id="gridFontSize"
                        name="gridFontSize"
                        label={t('profile.gridFontSize')}
                        sx={{ width: '90%' }}
                    >
                        {[...Array(9).keys()].map((o, ndx) =>
                            <MenuItem key={ndx} value={o+10}>
                                {`${o+10} px`}
                            </MenuItem>
                        )}
                    </FormSelect>

                </FormProperty>

                <FormProperty>
                    <FormSelect
                        id="gridCellPadding"
                        name="gridCellPadding"
                        label={t('profile.gridCellPadding')}
                        sx={{ width: '90%' }}
                    >
                        {[...Array(9).keys()].map((o, ndx) =>
                            <MenuItem key={ndx} value={o}>
                                {`${o} px`}
                            </MenuItem>
                        )}
                    </FormSelect>

                </FormProperty>

                <FormProperty>
                    <FormSelect
                        id="pagesize"
                        name="pageSize"
                        label={t('profile.gridPageSize')}
                        sx={{ width: '90%' }}

                    >
                        {[50, 100, 250].map((o, ndx) =>
                            <MenuItem key={ndx} value={o}>
                                {`${o} Rows`}
                            </MenuItem>
                        )}
                    </FormSelect>

                </FormProperty>

            </FormPropertySection>

            <FormPropertySection label="">
                <FormProperty>
                    <Box
                        sx={{
                            height: "auto",
                            width: "90%"
                        }}
                    >
                        <SampleGrid spacing={gridCellPadding} fontSize={gridFontSize} />
                    </Box>
                </FormProperty>

            </FormPropertySection>

            <FormPropertySection label={t('profile.summaryListingPreferences')} 
                    sx={{ 
                        whiteSpace: 'nowrap' 
                    }}>
                {!isPhone && <FormProperty>
                    <FormCheckbox
                        name="functionIcons"
                        label={t('profile.displayFunctionIcons')}
                        sx={{width: '90%'}}
                        
                    />

                </FormProperty>
                }

                <FormProperty>
                    <FormCheckbox
                        name="alarmsAtTop"
                        label={t('profile.displayAlarmsTop')}
                        sx={{ width: '90%' }}
                    />

                </FormProperty>

                <FormProperty>
                    <FormCheckbox
                        name="outletsFirst"
                        label={t('profile.displayOutletsFirst')}
                        sx={{ width: '90%' }}
                    />
                </FormProperty>

            </FormPropertySection>

            <FormPropertySection label={t('profile.marketingReportPreferences')}>
                <FormProperty>
                    <FormSelect
                        name="sortDates"
                        id="sortdates"
                        label={t('profile.sortDates.label')}
                        sx={{ width: '90%' }}
                    >
                        <MenuItem value={1}>
                            {t('profile.sortDates.left')}
                        </MenuItem>
                        <MenuItem value={2}>
                            {t('profile.sortDates.right')}
                        </MenuItem>
                    </FormSelect>

                </FormProperty>

            </FormPropertySection>

            <FormPropertySection label={t('profile.graphPreferences')}>
                <FormProperty>
                    <FormSelect
                        name="graphTooltip"
                        id="dataTooltip"
                        label={t('profile.dataTooltip.label')}
                        sx={{ width: '90%' }}
                    >
                        <MenuItem value={0}>
                            {t('profile.dataTooltip.shared')}
                        </MenuItem>
                        <MenuItem value={1}>
                            {t('profile.dataTooltip.separate')}
                        </MenuItem>
                    </FormSelect>

                </FormProperty>

                <FormProperty>
                    <FormSelect
                        name="graphsInViewPort"
                        id="graphsInViewPort"
                        label={t('profile.defaultStackedView.label')}
                        sx={{ width: '90%' }}
                    >
                        {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((o, ndx) => 
                            <MenuItem key={ndx} value={o}>
                                {o}
                            </MenuItem>

                        )}
                    </FormSelect>
                </FormProperty>

            </FormPropertySection>



            <FormPropertySection label={t('profile.groupPreferences')}>
                <FormProperty>
                    
                    <FormSelect
                        name="defaultGroupView"
                        label={t('profile.defaultView')}
                        sx={{ width: '90%' }}
                    >
                        {
                        groupViews.map((o, ndx) => (
                            <MenuItem key={ndx} value={o.Value}>
                                {o.Key}
                            </MenuItem>
                        ))
                        }
                    </FormSelect>

                </FormProperty>
            </FormPropertySection>

            <FormPropertySection label={t('profile.deviceDetailPreferences')}>
                <FormProperty>
                    <FormSelect
                        name="deviceView"
                        id="devmobileview"
                        label={t('profile.deviceViewMobile.label')}
                        sx={{ width: '90%' }}
                    >
                        <MenuItem value={0}>
                            {t('profile.deviceViewMobile.all')}
                        </MenuItem>
                        <MenuItem value={1}>
                            {t('profile.deviceViewMobile.summary')}
                        </MenuItem>
                    </FormSelect>

                </FormProperty>

                <FormProperty>
                    <FormSelect
                        name="collapseTagGroups"
                        id="collapsetaggroups"
                        label={t('profile.collapseTagGroups.label')}
                        sx={{ width: '90%' }}
                    >
                        <MenuItem value={1}>
                            {t('profile.collapseTagGroups.collapsed')}
                        </MenuItem>
                        <MenuItem value={0}>
                            {t('profile.collapseTagGroups.expanded')}
                        </MenuItem>
                    </FormSelect>

                </FormProperty>



                <FormProperty>
                    
                    <FormSelect
                        name="defaultDeviceView"
                        label={t('profile.defaultView')}
                        sx={{ width: '90%' }}
                    >
                        {
                        deviceViews.map((o, ndx) => (
                            <MenuItem key={ndx} value={o.Value}>
                                {o.Key}
                            </MenuItem>
                        ))
                        }
                    </FormSelect>

                </FormProperty>


            </FormPropertySection>

            <FormPropertySection label={t('profile.gaugeSheetPreferences')}>

                <FormProperty>

                    <FormSelect
                        name="gaugeSheetViewMode"
                        label={t('profile.gaugeSheetView')}
                        sx={{ width: '90%' }}
                    >
                        <MenuItem value={GaugeSheetViewMode.Spreadsheet}>
                            {t('liquidProd.mode.spreadsheet')}
                        </MenuItem>
                        <MenuItem value={GaugeSheetViewMode.Form}>
                            {t('liquidProd.mode.form')}
                        </MenuItem>
                    </FormSelect>

                    
                </FormProperty>

                <FormProperty>
                    <FormCheckbox
                        name="gaugeSheetShowDevice"
                        label={t("liquidProd.showDevice")}
                        onChange={(_ev, checked) => onShowDeviceChange(checked)}
                    />
                </FormProperty>

                <FormProperty>
                    <FormCheckbox
                        name="gaugeSheetShowTanks"
                        label={t("liquidProd.showTanks")}
                        onChange={(_ev, checked) => onShowTanksChange(checked)}

                    />
                    
                </FormProperty>


            </FormPropertySection>

            <FormPropertySection label={t('profile.tagDetailPreferences')} >
                <FormProperty>
                    <FormSelect
                        name="tagView"
                        id="tagview"
                        label={t('profile.deviceViewMobile.label')}
                        sx={{ width: '90%' }}
                    >
                        <MenuItem value={0}>
                            {t('profile.tagHistory.chart')}
                        </MenuItem>
                        <MenuItem value={1}>
                            {t('profile.tagHistory.grid')}
                        </MenuItem>
                    </FormSelect>

                </FormProperty>
            </FormPropertySection>


        </>
        : null;
}



